import type { SessionService } from '@efdigital/ll-addon-auth';
import { CoachCard } from '@efdigital/ll-addon-coaching';
import { getStatusIcon } from '@efdigital/ll-addon-coaching/utils/coaching-sessions/coach-partnership-status';
import type { CoachProfile } from '@efdigital/ll-addon-data';
import { Heading } from '@efdigital/ll-addon-ui';
import type { CoachMatch } from '@efdigital/ll-cdk-coaching-ai-matching-api-types';
import { Spinner } from '@efdigital/ll-xceed-v2/components/application/spinner.gts';
import { CoachPartnershipActions } from '@efdigital/ll-xceed-v2/components/coaching/coachee/coach-partnership-actions.gts';
import {
  CoachesBrowse,
  type CoachProfileWithRelevance,
} from '@efdigital/ll-xceed-v2/components/coaching/coachee/coaches-browse.gts';
import type CoachingCoacheeCohortCoachesIndexController from '@efdigital/ll-xceed-v2/controllers/coaching/coachee/cohort/coaches/index';
import { queryCoachProfilesByUserIds } from '@efdigital/ll-xceed-v2/data/builders/coaching/coach-profiles';
import type { CoachesIndexModel } from '@efdigital/ll-xceed-v2/routes/coaching/coachee/cohort/coaches/index';
import type CoacheeService from '@efdigital/ll-xceed-v2/services/coachee';
import { array } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { Request } from '@warp-drive/ember';
import { pageTitle } from 'ember-page-title';
import { cell, resource, use } from 'ember-resources';

/**
 * Bounded retry delays (ms) for the coach-matches fetch. The coachee's onboarding
 * answer embedding is generated asynchronously after quiz submission, so the very
 * first request made right after finishing the quiz can 404 until that finishes.
 * Total window: 14s across 4 retries, then give up.
 */
const RETRY_DELAYS_MS = [1500, 2500, 4000, 6000];

interface CoachesIndexSignature {
  Args: {
    model: CoachesIndexModel;
    controller: CoachingCoacheeCohortCoachesIndexController;
  };
}

interface RelevanceMatchesState {
  matches: CoachMatch[];
  pending: boolean;
  failed: boolean;
}

function showRatingBadge(relevance: number): boolean {
  return relevance >= 70;
}

export default class CoachesIndex extends Component<CoachesIndexSignature> {
  @service declare session: SessionService;
  @service declare coachee: CoacheeService;

  @use relevanceMatches = resource<RelevanceMatchesState>(({ on }) => {
    const state = cell<RelevanceMatchesState>({
      matches: [],
      pending: true,
      failed: false,
    });
    const cohortId = this.args.model.cohort_id;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const attempt = async (delaysRemaining: number[]): Promise<void> => {
      try {
        const matches = await this.coachee.tryGetRelevanceMatches(cohortId);
        if (!cancelled) {
          state.set({ matches, pending: false, failed: false });
        }
        return;
      } catch (error) {
        console.error('[coach-matches] failed to fetch relevance', error);
      }

      if (cancelled) {
        return;
      }

      const [delay, ...rest] = delaysRemaining;
      if (delay === undefined) {
        state.set({ matches: [], pending: false, failed: true });
        return;
      }

      timer = setTimeout(() => void attempt(rest), delay);
    };

    void attempt(RETRY_DELAYS_MS);

    on.cleanup(() => {
      cancelled = true;
      clearTimeout(timer);
    });

    return () => state.current;
  });

  coachesWithRelevance(
    coachProfiles: CoachProfile[],
    relevanceMatches: CoachMatch[]
  ): CoachProfileWithRelevance[] {
    return coachProfiles.map((coachProfile) => ({
      coachProfile,
      relevance:
        relevanceMatches.find((match) => match.coachId === coachProfile.user)
          ?.relevance ?? 0,
    }));
  }

  get coachUserIds(): string[] {
    return this.relevanceMatches.matches.map((match) => match.coachId);
  }

  <template>
    {{pageTitle "Coaches"}}

    <div class="container my-3">
      {{#if this.relevanceMatches.pending}}
        <Spinner />
      {{else if this.relevanceMatches.failed}}
        <div class="alert alert-danger">
          There was an error fetching the list of coaches. Please try again
          later. If the problem persists, please contact
          <a
            href="mailto:v-support@hultashridge.com"
          >v-support@hultashridge.com</a>.
        </div>
      {{else}}
        <Request @query={{queryCoachProfilesByUserIds this.coachUserIds}}>
          <:content as |coachProfiles|>
            {{#let
              (this.coachesWithRelevance
                coachProfiles.data this.relevanceMatches.matches
              )
              as |pool|
            }}
              <Heading
                @size="sm"
                @text="Browse Coaches"
                @secondary="Apply filters to narrow down the list and find the perfect coach for you."
                class="mb-4"
              />

              <CoachesBrowse
                @pool={{pool}}
                @searchQuery={{@controller.searchQuery}}
                @sort={{@controller.validatedSort}}
                @specialty={{@controller.specialty}}
                @language={{@controller.language}}
                @industry={{@controller.industry}}
                @country={{@controller.country}}
                @onSearchQueryChange={{@controller.setSearchQuery}}
                @onSortChange={{@controller.setSort}}
                @onSpecialtyChange={{@controller.setSpecialty}}
                @onLanguageChange={{@controller.setLanguage}}
                @onIndustryChange={{@controller.setIndustry}}
                @onCountryChange={{@controller.setCountry}}
                class="mb-4"
              >
                <:matches as |matches|>
                  {{#if matches.length}}

                    <div class="row g-3 g-xl-4 align-items-stretch">
                      {{#each matches as |match|}}
                        {{#let
                          (@model.planner.getStatusForCoach
                            match.coachProfile.user
                          )
                          as |status|
                        }}
                          {{#let
                            (@model.planner.hasUncancelledChemistrySession
                              match.coachProfile.user
                            )
                            as |hasChemistrySession|
                          }}
                            {{#let
                              (getStatusIcon status hasChemistrySession)
                              as |icon|
                            }}
                              <div class="col-12 col-md-6 col-xxl-4">
                                <CoachCard
                                  class="h-100 w-100"
                                  @coach={{match.coachProfile}}
                                  @cardClass="h-100 w-100 rounded-4 shadow-sm"
                                  @footerClass="d-flex flex-row justify-content-between gap-1 align-items-center"
                                  @color={{icon.color}}
                                >
                                  <:badge>
                                    {{#if (showRatingBadge match.relevance)}}
                                      <span
                                        class="badge rounded-pill text-bg-white"
                                      >{{Math.round match.relevance}}% match</span>
                                    {{/if}}
                                    {{#if icon}}
                                      <span
                                        class="badge rounded-pill bg-{{icon.color}}-subtle text-dark"
                                      >
                                        <iconify-icon
                                          icon="{{icon.icon}}"
                                          inline
                                        />
                                        {{icon.label}}
                                      </span>
                                    {{/if}}
                                  </:badge>
                                  <:actionsBack>
                                    <CoachPartnershipActions
                                      @planner={{@model.planner}}
                                      @coachUserId={{match.coachProfile.user}}
                                      @buttonClass="btn btn-sm text-nowrap"
                                      @collapseMultipleActions={{true}}
                                    />
                                  </:actionsBack>
                                  <:actionsFront>
                                    <LinkTo
                                      @route="coaching.coachee.cohort.coaches.coach"
                                      @models={{array
                                        @model.program_id
                                        @model.cohort_id
                                        match.coachProfile.user
                                      }}
                                      class="btn btn-{{if
                                          icon.color
                                          icon.color
                                          'primary'
                                        }}
                                        btn-sm"
                                    >Full profile
                                      <iconify-icon
                                        icon="ph:arrow-right-bold"
                                        inline
                                      /></LinkTo>
                                  </:actionsFront>
                                </CoachCard>
                              </div>
                            {{/let}}
                          {{/let}}
                        {{/let}}
                      {{/each}}
                    </div>
                  {{else}}
                    <div class="alert alert-primary">
                      No coaches found that match your search criteria.
                    </div>
                  {{/if}}
                </:matches>
              </CoachesBrowse>
            {{/let}}
          </:content>
        </Request>
      {{/if}}
    </div>
  </template>
}
